import { useState, useEffect, useCallback } from 'react';
import { UserProfile } from '../types';
import { saveUserProfile } from '../services/userProfileService';

interface ProfileUpdate {
  field: string;
  value: any;
  source: 'explicit' | 'extracted' | 'inferred';
}

export const useProgressiveProfile = (initialProfile: UserProfile | null) => {
  const [profile, setProfile] = useState<UserProfile | null>(initialProfile);
  const [pendingUpdates, setPendingUpdates] = useState<ProfileUpdate[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const updateProfile = useCallback((update: ProfileUpdate) => {
    setProfile(prev => {
      if (!prev || prev[update.field] === update.value) return prev;
      console.log(`🔄 Profile update queued: ${update.field} = ${update.value} (source: ${update.source})`);
      setPendingUpdates(prevUpdates => [...prevUpdates, update]);
      return { ...prev, [update.field]: update.value };
    });
  }, []);

  const savePendingUpdates = useCallback(async () => {
    if (!profile) return;
    setIsSaving(true);
    console.log(`💾 Saving profile with ${pendingUpdates.length} pending updates...`);
    try {
      await saveUserProfile(profile);
      setPendingUpdates([]);
      console.log('✅ Profile saved successfully');
    } catch (err) {
      console.warn('❌ Failed to save profile:', err);
    } finally {
      setIsSaving(false);
    }
  }, [profile, pendingUpdates]);

  useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const timer = setTimeout(savePendingUpdates, 1000); // throttle saves
    return () => clearTimeout(timer);
  }, [pendingUpdates, savePendingUpdates]);

  return {
    profile,
    updateProfile,
    isSaving
  };
};